import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";

import {
  getServiceLeads,
  updateLeadStatus,
  deleteServiceLead,
} from "../../Backend/querys";
import LeadModal from "./LeadModal";
import LeadsTable from "./LeadsTable";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const ServicesLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  // --- FETCH LEADS ---
  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    const result = await getServiceLeads();

    if (result.success) {
      setLeads(result.data);
    } else {
      Swal.fire({
        icon: "error",
        title: "Sync Failed",
        text: "Could not fetch service leads.",
      });
    }
    setLoading(false);
  };

  // --- ACTIONS ---
  const handleStatusChange = async (id, newStatus) => {
    setLeads((prev) =>
      prev.map((lead) =>
        lead.id === id ? { ...lead, status: newStatus } : lead,
      ),
    );

    const result = await updateLeadStatus(id, newStatus);
    if (!result.success) {
      console.error("Status update failed");
      fetchLeads();
    }
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Delete Lead?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#0B1C2D",
    });

    if (isConfirmed) {
      setLeads((prev) => prev.filter((lead) => lead.id !== id));
      if (selectedLead?.id === id) setSelectedLead(null);

      const result = await deleteServiceLead(id);
      if (!result.success) {
        console.error("Delete failed");
        fetchLeads();
      }
    }
  };

  const handleReply = (lead) => {
    const email = lead.email;
    const senderName = lead.sender || "there";
    const subjectText = lead.formTitle || "Your Project Inquiry";

    if (!email || !email.includes("@")) {
      return Swal.fire({
        icon: "error",
        title: "Invalid Email",
        confirmButtonColor: "#0B1C2D",
      });
    }

    const subject = `RE: ${subjectText}`;
    const body = [
      `Hi ${senderName},`,
      ``,
      `Thank you for reaching out regarding your upcoming project. We've reviewed your requirements and would love to discuss the details and budget (Currently marked as ${lead.budget}).`,
      ``,
      `When are you available for a quick 15-minute discovery call?`,
      ``,
      `Best regards,`,
      `The ContactHQ Studio Team`,
    ].join("\r\n");

    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    const mailWindow = window.open(mailtoUrl, "_self");
    if (
      !mailWindow ||
      mailWindow.closed ||
      typeof mailWindow.closed === "undefined"
    ) {
      const tempAnchor = document.createElement("a");
      tempAnchor.href = mailtoUrl;
      tempAnchor.style.display = "none";
      document.body.appendChild(tempAnchor);
      tempAnchor.click();
      document.body.removeChild(tempAnchor);
    }
  };

  // --- STYLING HELPERS ---
  const getStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "pending") return "bg-amber-100 text-amber-700 border-amber-200";
    if (s === "contacted") return "bg-blue-100 text-blue-700 border-blue-200";
    if (s === "converted")
      return "bg-green-100 text-green-700 border-green-200";
    if (s === "rejected") return "bg-slate-100 text-slate-500 border-slate-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-10 gap-4">
        <div className="hidden lg:block">
          <h1 className="text-2xl md:text-3xl font-black text-[#0B1C2D] tracking-tighter uppercase flex items-center gap-3">
            <span className="material-symbols-outlined text-3xl md:text-4xl text-[#E63946]">
              handshake
            </span>
            Service Leads
          </h1>
          <p className="text-slate-500 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1 ml-1">
            Manage incoming project inquiries & client quotes
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-slate-200 flex items-center gap-3 w-full sm:w-auto justify-center"
        >
          <div className="size-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
          <span className="text-xs font-black text-[#0B1C2D] uppercase tracking-widest">
            {leads.length} Active Leads
          </span>
        </motion.div>
      </div>

      {/* RENDER TABLE COMPONENT */}
      <LeadsTable
        leads={leads}
        loading={loading}
        onView={setSelectedLead}
        onReply={handleReply}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        getStatusStyle={getStatusStyle}
      />

      {/* RENDER MODAL COMPONENT */}
      <AnimatePresence>
        {selectedLead && (
          <LeadModal
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onReply={handleReply}
            getStatusStyle={getStatusStyle}
          />
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default ServicesLeads;
